import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { TslGlobalPhysicalPosition, TslToastrService } from '@tesla/ng-tds';
import { FileUploadService } from 'app/common/services/fileUpload.service';
import { OnboardingTab, TaskStatus } from 'app/profile/models/onboarding.model';
import { InsuranceCoverage, InsuranceMaster, RequestFormSection, SupplierDocument } from 'app/profile/models/supplier.model';
import { SupplierTaskResponse, SupplierTaskStep } from 'app/profile/models/suppliertasks.model';
import { SupplierTaskService } from 'app/profile/services/supplier-task.service';
import { SupplierStateService } from 'app/profile/services/supplierstate.service';
import { I18nService } from 'app/shared/services/i18n.service';
import * as saveAs from 'file-saver';
import * as _ from 'lodash'
import { forkJoin, Observable } from 'rxjs';

@Component({
  selector: 'app-insurance-task-wrapper',
  templateUrl: './insurance-task-wrapper.component.html',
  styleUrls: ['./insurance-task-wrapper.component.scss']
})
export class InsuranceTaskWrapperComponent implements OnInit, OnDestroy {

  @Input('supplierCode') set _supplierCode(val: string) { if (val && val.trim() != "") this.supplierCode = val; };
  @Input('taskId') set _taskId(val: number) { if (val) this.taskId = val; };
  @Input('isSidePanelMode') isSidePanelMode: boolean = false;
  @Input() isRequired: boolean = false;
  @Output() onSave: EventEmitter<boolean> = new EventEmitter<boolean>();
  supplierCode: string;
  taskId: number;
  onboardingTab: OnboardingTab = OnboardingTab.INSURANCE;
  displayedColumns: string[] = ['policyType', 'expirationDate', 'document', 'action'];
  dataSource = new MatTableDataSource<any>([]);
  displayList: InsuranceMaster[] = [];
  selectedList: string[] = [];
  rejectComment:string;
  rejectionReasons:string;
  errorMessage: string;
  minDate: Date = new Date();
  taskInfo: SupplierTaskStep;
  taskStatus: string;
  rejectionDetails: any;
  isLoaded: boolean = false;

  policyTypeList = [
    {displayName:"Commercial General Liability", id: 0},
    {displayName: "Automobile Liability", id: 1},
    {displayName: "Umbrella (Excess) Liability", id: 2},
    {displayName: "Workers Compensation Liability", id: 3},
    {displayName: "On Hook/Cargo Coverage", id: 4},
    {displayName: "Garagekeepers Legal Liability", id: 5},
    {displayName: "Other", id: 6}
  ];

  constructor(
    private supplierStateService: SupplierStateService,
    private fileUploadService: FileUploadService,
    private toasterService: TslToastrService,
    private route: ActivatedRoute,
    private taskService: SupplierTaskService,
    private translate: I18nService,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.route?.parent?.params.subscribe(params => {
      if (params?.code && params?.portalTaskId) {
        this.supplierCode = params.code;
        this.taskId = params.portalTaskId;
      }
    });
    this.taskService.getSupplierTaskStep(this.supplierCode, this.taskId, this.onboardingTab).subscribe(taskInfo => {
      if (taskInfo && taskInfo.taskRequestMetadata) {
        this.taskInfo = taskInfo;
        this.taskStatus = taskInfo.taskStatus;
        this.rejectionDetails = taskInfo?.rejectionDetails;
        if (this.taskStatus != TaskStatus.Completed) {
          let insurances;
          if(taskInfo.portalTaskResponse != null) insurances = taskInfo.portalTaskResponse;
          else if(taskInfo.taskRequestMetadata?.Insurance) insurances = taskInfo.taskRequestMetadata.Insurance
          if(insurances) {
            this.displayList = insurances.map(insurance => {
              return { 
                coverages: insurance?.Coverages?.map(coverage => {
                  return {
                    expiryDate: coverage.ExpiryDate,
                    coverageType: coverage.CoverageType,
                    comment: coverage.Comment
                  }
                }),
                document: insurance.Document,
                documentId: insurance.DocumentId,
                id: insurance.ID,
                operation: insurance.Operation,
                stageID: insurance.StageID
              }
             });
          }
        }
        this.initTable();
        this.isLoaded = true;
      }
    });
    
  }

  initTable() {
    this.policyTypeList = _.cloneDeep(this.policyTypeList);
    if(!this.displayList?.length) this.displayList = this.isRequired? [new InsuranceMaster()] : [];
    this.displayList.forEach(x => {
      if(x.coverages?.length) x.coverages.forEach(coverage => {if(coverage.coverageType && coverage.coverageType?.trim() != "" && coverage.coverageType != "Other") this.selectedList.push(coverage.coverageType)});
    });
    this.dataSource.data = this.displayList;
  }

  onCoverageTypeChange(event, coverage: InsuranceCoverage, index: number, insurance: InsuranceMaster) {
    if(coverage.coverageType && coverage.coverageType != "Other") {
      let deleteIndex = this.selectedList?.findIndex(x => x == coverage.coverageType);
      if(deleteIndex > -1) this.selectedList.splice(deleteIndex, 1);
    } else if(coverage.coverageType == "Other") {
      coverage.comment = null;
    }
    if(event.value) {
      coverage.coverageType = event.value;
      if(!this.selectedList) this.selectedList = [];
      if(event.value != "Other") this.selectedList.push(event.value);
    }
    insurance.errorMessage = null;
  }

  getDocumentOptionList(coverage: InsuranceCoverage) {
    return this.policyTypeList.filter(x => (!this.selectedList?.includes(x.displayName) || x.displayName == coverage.coverageType));
  }

  uploadFile(event, element: InsuranceMaster) {
    this.errorMessage = null;
    if(element && event?.target?.files?.length) {
      element.document = new SupplierDocument();
      element.document.File = event.target.files[0];
      element.document.FileName = element.document.File.name;
    }
    element.errorMessage = null;
  }

  downloadFile(file: SupplierDocument) {
    if(!file || !file.FileName) return;
    if (file.DocumentID) {
      if (!file.ReferenceSystem) {
        if (file.DocumentID && file.DocumentID.length > 30) {
          file.ReferenceSystem = 'DMS';
        } else {
          file.ReferenceSystem = 'SharePoint';
        }
        if (file.ESignAgreementId) {
          file.ReferenceSystem = 'Adobe';
          file.DocumentID = file.ESignAgreementId;
        }
      }
      this.supplierStateService.downloadFile(
        this.supplierCode,
        file.ReferenceSystem,
        file.DocumentID,
        file.DocumentVersion)
        .subscribe(resultBlob => {
          saveAs(resultBlob, `${this.supplierCode} - ${file.FileName}`);
        }, (error) => {
          this.toasterService.error(`Something went wrong. Unable to download!`, null, { position: TslGlobalPhysicalPosition.BOTTOM_RIGHT, duration: 5000 });
        }
      );
    } else {
      saveAs(file.File);
    }
  }

  onDeleteFile(element: InsuranceMaster) {
    this.errorMessage = null;
    if(element.document) element.document = null;
  }

  onAddRow() {
    this.errorMessage = null;
    if(!this.displayList?.length) this.displayList = [];
    this.displayList.push(new InsuranceMaster());
    this.dataSource.data = this.displayList;
  }

  onDeleteRow(index) {
    this.errorMessage = null;
    if(this.displayList[index]?.coverages?.length) {
      this.displayList[index].coverages.forEach(coverage => {
        if(coverage.coverageType && coverage.coverageType != "Other") {
          let deleteIndex = this.selectedList?.findIndex(x => x == coverage.coverageType);
          if(deleteIndex > -1) this.selectedList.splice(deleteIndex, 1);
        }
      })
    }
    this.displayList.splice(index, 1);
    this.dataSource.data = this.displayList;
  }

  onAddCoverageRow(insurance: InsuranceMaster) {
    if(!insurance?.coverages) insurance.coverages = [];
    insurance.coverages.push(new InsuranceCoverage());
    insurance.errorMessage = null;
  }

  onDeleteCoverageRow(insurance: InsuranceMaster, index: number) {
    this.errorMessage = null;
    if(insurance?.coverages?.length && insurance?.coverages[index]) {
      if(insurance.coverages[index].coverageType && insurance.coverages[index].coverageType != "Other") {
        let deleteIndex = this.selectedList?.findIndex(x => x == insurance.coverages[index].coverageType);
        if(deleteIndex > -1) this.selectedList.splice(deleteIndex, 1);
      }
    }
    insurance.coverages.splice(index, 1);
    insurance.errorMessage = null;
    this.dataSource.data = this.displayList;
  }

  isValid(): boolean {
    if(!this.displayList?.length) {
      if(!this.isRequired) return true;
      this.errorMessage = "Please add at least one document."
      return false;
    }
    let isValid = true;
    this.displayList.forEach(insurance => {
      if(!(insurance?.document?.DocumentID && insurance?.document?.DocumentVersion) && !insurance?.document?.File?.name) {
        insurance.errorMessage = "Please upload file.";
        isValid = false;
      }
      if(!insurance.coverages?.length) {
        insurance.errorMessage = "Please add policy/coverage type.";
        isValid = false;
      } else {
        insurance.coverages.forEach(coverage => {
          if(!coverage.coverageType) {
            insurance.errorMessage = "Please select policy/coverage type.";
            isValid = false;
          } else if(!coverage.expiryDate) {
            insurance.errorMessage = "Please add policy/coverage expiration date.";
            isValid = false;
          } else if(coverage.coverageType == "Other" && !coverage.comment) {
            insurance.errorMessage = "Please provide policy/coverage name.";
            isValid = false;
          }
        });
      }
    });
    if(!isValid) return false;
    return true;
  }

  onSubmit() {
    if (!this.isValid()) return;
    let final = _.cloneDeep(this.displayList)
    const uploadList = final?.filter(x => !!x?.document?.File?.name);
    let documentObservables: Observable<any>[] = [];
    uploadList.forEach(file => {
      file.document.ReferenceSystem = "DMS";
      file.document.DocumentType = "CI";
      documentObservables.push(this.fileUploadService.uploadFileToServer([file.document.File as File], this.supplierCode));
    });
    if (documentObservables.length) {
      forkJoin(documentObservables).subscribe(files => {
        if (files?.length) {
          files.forEach((docRes, index) => {
            if (docRes["documentID"] != null) {
              uploadList[index].document.DocumentID = docRes["documentID"];
              uploadList[index].document.DocumentVersion = docRes["documentVersion"];
              uploadList[index].document.FileName = docRes["fileName"];
              uploadList[index].document.File = null;
            }
            else { 
              this.toasterService.error(this.translate.get('Something went wrong! Please resubmit.'));
              throw new Error('Something went wrong! Please resubmit.');
            }
          });
          this.callResolveFunction(final);
        }
      });
    }
    else { 
      this.callResolveFunction(final);
    }
  }

  callResolveFunction(taskResponse: any) { 
    taskResponse.forEach(x => delete x.errorMessage);
    var response: SupplierTaskResponse = {
      id: this.taskId,
      supplierCode: this.supplierCode,
      taskResponseJson: JSON.stringify(taskResponse)
    }
    this.taskService.postSupplierTaskStepResponse(this.supplierCode, response).subscribe(res => {
      if (res) {
        this.toasterService.success(this.translate.get('Submitted successfully!'));
        if (this.isSidePanelMode) this.onSave.emit(true);
        else this.router.navigate([`${this.supplierCode}`, "Onboarding", "Home"]);
      }
      else {
        this.toasterService.error(this.translate.get('Something went wrong!'));
      }
    });
  }
  ngOnDestroy(): void {
    
  }
}
